// ─── Enums ───────────────────────────────────────────────────────────────────

export enum Role {
  PLATFORM_SUPER_ADMIN = 'PLATFORM_SUPER_ADMIN',
  TENANT_ADMIN = 'TENANT_ADMIN',
  MANAGER = 'MANAGER',
  EMPLOYEE = 'EMPLOYEE',
}

export enum TenantStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED',
}

export enum EmployeeStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum VisitStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  MISSED = 'MISSED',
  CANCELLED = 'CANCELLED',
}

export enum Priority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

export enum CustomerStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum BillingCycle {
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
}

export enum WorkdayStatus {
  WORKING = 'WORKING',
  COMPLETED = 'COMPLETED',
  ABSENT = 'ABSENT',
}

// ─── Core Entities ───────────────────────────────────────────────────────────

export interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
  tenantId?: string;
  employeeId?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export enum PlanStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export interface Plan {
  id: string;
  name: string;
  employeeLimit: number;
  monthlyPrice: number;
  yearlyPrice: number;
  status: PlanStatus | 'ACTIVE' | 'INACTIVE';
  features: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: TenantStatus;
  planId: string;
  plan?: Pick<Plan, 'id' | 'name'> & Partial<Plan>;
  employeeLimit: number;
  employeeCount?: number;
  activeEmployees?: number;
  monthlyRevenue?: number;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeeLocation {
  latitude: number;
  longitude: number;
  timestamp: string;
  accuracy?: number;
  batteryLevel?: number;
  speed?: number;
}

export interface Employee {
  id: string;
  tenantId: string;
  userId?: string;
  employeeCode: string;
  name: string;
  email: string;
  mobile: string;
  department?: string;
  managerId?: string;
  manager?: Pick<Employee, 'id' | 'name' | 'email'>;
  status: EmployeeStatus;
  lastLocation?: EmployeeLocation;
  todayVisitsCount?: number;
  onDuty?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Customer {
  id: string;
  tenantId: string;
  name: string;
  contactPerson?: string;
  mobile?: string;
  email?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  status: CustomerStatus;
  geofence?: Geofence;
  createdAt: string;
  updatedAt: string;
}

export interface Geofence {
  id: string;
  tenantId: string;
  customerId: string;
  customer?: Customer;
  radius: number;
  latitude: number;
  longitude: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Workday {
  id: string;
  tenantId: string;
  employeeId: string;
  employee?: Employee;
  date: string;
  checkInTime?: string;
  checkOutTime?: string;
  totalWorkMinutes?: number;
  totalDistanceKm?: number;
  visitsCompleted?: number;
  visitsMissed?: number;
  status: WorkdayStatus;
}

export interface VisitAssignment {
  id: string;
  tenantId: string;
  employeeId: string;
  employee?: Employee;
  customerId: string;
  customer?: Customer;
  assignedDate: string;
  scheduledTime?: string;
  priority: Priority;
  status: VisitStatus;
  notes?: string;
  completedAt?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface VisitEvent {
  id: string;
  tenantId: string;
  visitAssignmentId: string;
  employeeId: string;
  customerId: string;
  geofenceId?: string;
  eventType: 'CHECK_IN' | 'CHECK_OUT';
  latitude: number;
  longitude: number;
  timestamp: string;
  accuracy?: number;
  durationMinutes?: number;
  notes?: string;
}

export interface TrackingEvent {
  id: string;
  tenantId: string;
  employeeId: string;
  employee?: Employee;
  workdayId: string;
  startTime: string;
  endTime?: string;
  gapMinutes?: number;
  reason?: string;
  isResolved: boolean;
  lastKnownLatitude?: number;
  lastKnownLongitude?: number;
  createdAt: string;
}

export interface Subscription {
  id: string;
  tenantId: string;
  planId: string;
  plan?: Plan;
  status: 'ACTIVE' | 'EXPIRED' | 'CANCELLED';
  startDate: string;
  endDate?: string;
  createdAt: string;
}

// ─── API Response Types ───────────────────────────────────────────────────────

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// ─── Auth Types ───────────────────────────────────────────────────────────────

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
}

export interface AuthResponse {
  user: User;
  tokens: AuthTokens;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
}

// ─── Dashboard Stats ──────────────────────────────────────────────────────────

export interface SuperAdminStats {
  totalTenants: number;
  activeTenants: number;
  inactiveTenants: number;
  suspendedTenants: number;
  totalEmployees: number;
  platformRevenue: number;
  recentTenants: Tenant[];
}

export interface TenantAdminStats {
  totalEmployees: number;
  activeToday: number;
  visitsCompleted: number;
  pendingVisits: number;
  missedVisits: number;
  trackingInterruptions: number;
}

export interface ManagerStats {
  myEmployees: number;
  activeToday: number;
  visitsCompleted: number;
  pendingVisits: number;
  teamActivity: Array<{
    employee: Employee;
    status: 'active' | 'idle' | 'offline';
    visitsToday: number;
  }>;
}

// ─── Report Types ─────────────────────────────────────────────────────────────

export interface DailyReportRow {
  employee: Employee;
  workday: Workday;
  date: string;
}

export interface VisitReportRow {
  visit: VisitAssignment;
  employee: Employee;
  customer: Customer;
  duration?: number;
}

export interface TrackingGapRow {
  event: TrackingEvent;
  employee: Employee;
  gapMinutes: number;
  isResolved: boolean;
}

// ─── Form Types ───────────────────────────────────────────────────────────────

export interface CreateTenantForm {
  name: string;
  slug: string;
  planId: string;
  employeeLimit: number;
  adminName?: string;
  adminEmail?: string;
  adminPassword?: string;
  adminMobile?: string;
}

export interface CreateEmployeeForm {
  name: string;
  email: string;
  mobile: string;
  employeeCode: string;
  department?: string;
  managerId?: string;
  password?: string;
}

export interface CreateCustomerForm {
  name: string;
  contactPerson?: string;
  mobile?: string;
  email?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  geofenceRadius?: number;
}

export interface CreateVisitForm {
  employeeId: string;
  customerId: string;
  assignedDate: string;
  scheduledTime?: string;
  priority: Priority;
  notes?: string;
}

export interface CreatePlanForm {
  name: string;
  employeeLimit: number;
  monthlyPrice: number;
  yearlyPrice: number;
  features?: Record<string, unknown>;
  status?: PlanStatus | 'ACTIVE' | 'INACTIVE';
}
